import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Toolbar,
  Chip,
  Divider,
  Paper,
} from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Refresh,
  LocalPharmacy,
  Description,
  Inbox as InboxIcon,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import { observer } from "mobx-react";
import { DocumentDto, DocumentDtoDocuTypeEnum } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { useInboxDocuments } from "../../stores/Documents/documents.queries";
import { useAssignDocumentToPatient, useFinalizeInboxDocument } from "../../stores/Documents/documents.mutations";
import { UserContext } from "../../stores/User/User.provider";
import useStore from "../../helpers/useStore";
import RxExtractionForm from "../Documents/ocr/RxExtractionForm";
import PatientMatchDialog from "../Documents/ocr/PatientMatchDialog";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// ── Types ────────────────────────────────────────────────────

interface DocumentViewData {
  mimeType: string;
  base64Data: string;
}

// ── Document type icon helper ────────────────────────────────

const getDocTypeIcon = (docuType?: string) => {
  if (docuType === DocumentDtoDocuTypeEnum.Rezept) return <LocalPharmacy fontSize="small" />;
  return <Description fontSize="small" />;
};

const isExtractable = (docuType?: string) =>
  docuType === DocumentDtoDocuTypeEnum.Rezept;

// ── Main Component ──────────────────────────────────────────

const InboxPage: React.FC = observer(() => {
  const UserStore = useStore(UserContext);
  const locationId = UserStore.user?.locationId;
  const { documents: documentsApi } = useApi();

  // Data
  const { data: inboxDocs, isLoading, refetch } = useInboxDocuments(locationId);
  const assignMutation = useAssignDocumentToPatient();
  const finalizeMutation = useFinalizeInboxDocument();

  // Selection state
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const selectedDoc = inboxDocs?.find((d) => d.id === selectedDocId) || null;

  // Patient matching state (per document)
  const [resolvedPatients, setResolvedPatients] = useState<Record<string, number>>({});
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const [extractedPatientInfo, setExtractedPatientInfo] = useState<{
    name: string | null;
    dob: string | null;
    insuranceNo: string | null;
  }>({ name: null, dob: null, insuranceNo: null });

  const currentPatientId = selectedDocId
    ? resolvedPatients[selectedDocId] ?? selectedDoc?.patientId ?? null
    : null;

  // Viewer state
  const [viewData, setViewData] = useState<DocumentViewData | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // PDF state
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(0.8);

  // Auto-select first document when list loads
  useEffect(() => {
    if (inboxDocs?.length && !selectedDocId) {
      setSelectedDocId(inboxDocs[0].id);
    }
  }, [inboxDocs]);

  // Load document view data when selection changes
  useEffect(() => {
    if (!selectedDocId) {
      setViewData(null);
      return;
    }
    let cancelled = false;
    setViewLoading(true);
    documentsApi
      .requestViewAccessForDocument(selectedDocId)
      .then((resp: any) => {
        if (!cancelled) {
          setViewData(resp as DocumentViewData);
          setCurrentPage(1);
        }
      })
      .catch(() => {
        if (!cancelled) setViewData(null);
      })
      .finally(() => {
        if (!cancelled) setViewLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedDocId]);

  // ── Render helpers ─────────────────────────────────────────

  const isPdf = viewData?.mimeType === "application/pdf";
  const isImage = viewData?.mimeType?.startsWith("image/");

  const handleDocumentSelect = (docId: string) => {
    setSelectedDocId(docId);
    setZoom(0.8);
    setNumPages(0);
    setCurrentPage(1);
  };

  const handleSaveSuccess = useCallback(async () => {
    // After Rx saved, finalize: move blob out of inbox and clear inboxLocationId
    if (selectedDocId) {
      await finalizeMutation.mutateAsync(selectedDocId);
    }
    refetch();
  }, [selectedDocId, finalizeMutation, refetch]);

  const handlePatientResolved = useCallback(
    async (patientId: number) => {
      if (!selectedDocId) return;
      // Assign patient to the document (keeps it in inbox until Rx is saved)
      setResolvedPatients((prev) => ({ ...prev, [selectedDocId]: patientId }));
      await assignMutation.mutateAsync({ documentId: selectedDocId, patientId });
    },
    [selectedDocId, assignMutation]
  );

  const handleOpenPatientDialog = useCallback(
    (name: string | null, dob: string | null, insuranceNo: string | null = null) => {
      setExtractedPatientInfo({ name, dob, insuranceNo });
      setPatientDialogOpen(true);
    },
    []
  );

  // ── Layout ─────────────────────────────────────────────────

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden" }}>
      {/* ── Left: Document List ─────────────────────────────── */}
      <Paper
        elevation={1}
        sx={{
          width: 280,
          minWidth: 280,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Toolbar variant="dense" sx={{ justifyContent: "space-between", minHeight: 48 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InboxIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Eingangskorb
            </Typography>
            {inboxDocs && (
              <Chip label={inboxDocs.length} size="small" color="primary" variant="outlined" />
            )}
          </Box>
          <IconButton size="small" onClick={() => refetch()}>
            <Refresh fontSize="small" />
          </IconButton>
        </Toolbar>
        <Divider />
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !inboxDocs?.length ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <InboxIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Keine Dokumente im Eingangskorb
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {inboxDocs.map((doc) => (
                <ListItemButton
                  key={doc.id}
                  selected={doc.id === selectedDocId}
                  onClick={() => handleDocumentSelect(doc.id)}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {getDocTypeIcon(doc.docuType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.label || doc.name?.split("/").pop() || "Dokument"}
                    secondary={new Date(doc.createdAt).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  {doc.extractedAt && (
                    <Chip label="OCR" size="small" color="success" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: "0.65rem" }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* ── Center: Document Viewer ─────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "grey.100",
        }}
      >
        {/* Zoom controls */}
        {selectedDoc && viewData && (
          <Toolbar variant="dense" sx={{ minHeight: 40, gap: 1, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}>
            <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.2, z - 0.1))}>
              <ZoomOut fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 40, textAlign: "center" }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, z + 0.1))}>
              <ZoomIn fontSize="small" />
            </IconButton>
            {isPdf && numPages > 1 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <IconButton size="small" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                  <NavigateBefore fontSize="small" />
                </IconButton>
                <Typography variant="caption">
                  {currentPage} / {numPages}
                </Typography>
                <IconButton size="small" disabled={currentPage >= numPages} onClick={() => setCurrentPage((p) => p + 1)}>
                  <NavigateNext fontSize="small" />
                </IconButton>
              </>
            )}
          </Toolbar>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start", p: 2 }}>
          {viewLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : !selectedDoc ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, color: "text.disabled" }}>
              <InboxIcon sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6">Dokument auswählen</Typography>
              <Typography variant="body2">Wählen Sie ein Dokument aus dem Eingangskorb</Typography>
            </Box>
          ) : viewData && isPdf ? (
            <Document
              file={`data:application/pdf;base64,${viewData.base64Data}`}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<CircularProgress />}
            >
              <Page pageNumber={currentPage} scale={zoom} />
            </Document>
          ) : viewData && isImage ? (
            <img
              src={`data:${viewData.mimeType};base64,${viewData.base64Data}`}
              alt="Document"
              style={{ maxWidth: `${zoom * 100}%`, height: "auto" }}
            />
          ) : viewData ? (
            <Typography color="text.secondary">Format nicht unterstützt: {viewData.mimeType}</Typography>
          ) : null}
        </Box>
      </Box>

      {/* ── Right: Extraction Form (for Rx documents) ──────── */}
      {selectedDoc && isExtractable(selectedDoc.docuType) && (
        <Paper
          elevation={1}
          sx={{
            width: 400,
            minWidth: 400,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            borderLeft: "1px solid",
            borderColor: "divider",
          }}
        >
          <RxExtractionForm
            key={selectedDoc.id}
            document={selectedDoc}
            patientId={currentPatientId}
            onSaveSuccess={handleSaveSuccess}
            onPatientResolved={handlePatientResolved}
            onOpenPatientSearch={handleOpenPatientDialog}
          />
        </Paper>
      )}

      {/* Patient Match Dialog */}
      <PatientMatchDialog
        open={patientDialogOpen}
        onClose={() => setPatientDialogOpen(false)}
        onPatientSelected={handlePatientResolved}
        extractedName={extractedPatientInfo.name}
        extractedDob={extractedPatientInfo.dob}
        extractedInsuranceNo={extractedPatientInfo.insuranceNo}
        locationId={locationId}
      />
    </Box>
  );
});

export default InboxPage;
